import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, Crosshair, Loader, Lock, MapPin, RefreshCw, ScrollText, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/ui/PageHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { SectionHeader } from '../components/ui/SectionHeader';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { LogbookEventDetail } from '../components/logbook/LogbookEventDetail';
import { LogbookEventList } from '../components/logbook/LogbookEventList';
import { LogbookOutcomeBadge } from '../components/logbook/LogbookBadges';
import { AddToCaseModal } from '../components/logbook/AddToCaseModal';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useLogbookServers } from '../hooks/useLogbookServers';
import { logbookApiService, LogbookApiError } from '../services/logbookApi';
import { canProtectLogbook } from '../utils/logbookPermissions';
import { actionMeta, hasKnownTime, relativeTime } from '../utils/logbook';
import { boxAround, filtersToParams, DEFAULT_LOGBOOK_FILTERS } from '../utils/logbookFilters';
import { groupEvents } from '../utils/logbookView';
import { nearbyHrefForEvent } from '../utils/logbookNearby';
import type { LogbookCase, LogbookEvent } from '../types/logbook';

/**
 * One evidence record, in full, with what surrounds it.
 *
 * A single record rarely decides anything on its own. What usually settles a report is the minute
 * around it at the same coordinates: the menu access before the withdrawal, the second break that
 * followed the first. That context loads alongside the record rather than as a separate search.
 */
export function LogbookEventView() {
  const { eventId = '' } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { identity } = useAuth();
  const canProtect = canProtectLogbook(identity);
  const { byId: serversById } = useLogbookServers();

  const [event, setEvent] = useState<LogbookEvent | null>(null);
  const [nearby, setNearby] = useState<LogbookEvent[]>([]);
  const [cases, setCases] = useState<LogbookCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const [error, setError] = useState<LogbookApiError | Error | null>(null);
  const [attaching, setAttaching] = useState<LogbookEvent[] | null>(null);
  const [nonce, setNonce] = useState(0);

  useDocumentTitle(event ? `${actionMeta(event.action).label} by ${event.actor_name || 'unattributed'}` : 'Logbook record');

  useEffect(() => {
    if (!eventId) return;
    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNearby([]);

    const run = async () => {
      try {
        const record = await logbookApiService.getEvent(eventId, controller.signal);
        if (cancelled) return;
        setEvent(record);
        setLoading(false);

        // The case ledger and the surrounding minute are context, not the record: neither failing
        // should take the record off screen.
        logbookApiService.listCases({ event_id: eventId, limit: 20 }, controller.signal)
          .then((result) => { if (!cancelled) setCases(result.cases ?? []); })
          .catch(() => undefined);

        if (hasKnownTime(record)) {
          setNearbyLoading(true);
          logbookApiService.search({
            servers: [record.server_id],
            dimension: record.dimension,
            box: boxAround(record.x, record.y, record.z, 16) ?? undefined,
            after: record.timestamp - 60_000,
            before: record.timestamp + 60_000,
            limit: 50,
          }, controller.signal)
            .then((result) => {
              if (cancelled) return;
              setNearby(result.events.filter((candidate) => candidate.event_id !== record.event_id));
            })
            .catch(() => undefined)
            .finally(() => { if (!cancelled) setNearbyLoading(false); });
        }
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        setEvent(null);
        setError(caught instanceof Error ? caught : new Error('That record could not be loaded.'));
        setLoading(false);
      }
    };

    void run();
    return () => { cancelled = true; controller.abort(); };
  }, [eventId, nonce]);

  const server = event ? serversById.get(event.server_id) : undefined;
  const meta = event ? actionMeta(event.action) : null;

  const investigateActor = useCallback(() => {
    if (!event) return;
    const params = filtersToParams({ ...DEFAULT_LOGBOOK_FILTERS, actorId: event.actor_id, windowId: '30d' });
    navigate(`/logbook?${params.toString()}`);
  }, [event, navigate]);

  const investigateArea = useCallback(() => {
    if (!event) return;
    const params = filtersToParams({
      ...DEFAULT_LOGBOOK_FILTERS,
      windowId: '7d',
      servers: [event.server_id],
      dimension: event.dimension,
      box: boxAround(event.x, event.y, event.z, 32),
    });
    navigate(`/logbook?${params.toString()}`);
  }, [event, navigate]);

  // The record itself is marked as held when any case already holds a copy of it.
  const heldIds = useMemo(() => new Set(cases.length > 0 ? [eventId] : []), [cases, eventId]);
  // Sixty seconds of context around one record is short enough to read whole: nothing is folded
  // away and nothing is filtered out, because the point of this list is what else was captured.
  const nearbyRuns = useMemo(() => groupEvents(nearby, 0), [nearby]);

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Moderation" title="Logbook record" backTo="/logbook" backLabel="Back to Logbook" icon={ScrollText} />
        <div className="glass-card p-5 space-y-3">
          <SkeletonLoader className="h-5 w-1/3" />
          <SkeletonLoader className="h-4 w-2/3" />
          <SkeletonLoader className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (error || !event) {
    const missing = error instanceof LogbookApiError && error.status === 404;
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Moderation" title="Logbook record" backTo="/logbook" backLabel="Back to Logbook" icon={ScrollText} />
        <EmptyState
          icon={missing ? ScrollText : AlertTriangle}
          tone={missing ? 'neutral' : 'danger'}
          title={missing ? 'This record is not available' : 'The record could not be loaded'}
          description={missing
            ? 'It may have passed retention, been rolled up, or belong to a server outside your scope.'
            : error?.message}
          action={(
            <button type="button" className="glass-button" onClick={() => setNonce((value) => value + 1)}>
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          )}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Moderation"
        title={meta?.label ?? event.action}
        description={`${meta?.source ?? 'Logbook'} capture on ${event.server_id}`}
        icon={ScrollText}
        backTo="/logbook"
        backLabel="Back to Logbook"
        titleAdornment={(
          <>
            <LogbookOutcomeBadge outcome={event.outcome} />
            {cases.length > 0 && <span className="badge badge-amber badge-dot">In a case</span>}
          </>
        )}
        meta={<span className="tabular-nums">{relativeTime(event.timestamp)}</span>}
        actions={(
          <>
            <button type="button" className="glass-button" onClick={investigateActor} disabled={!event.actor_id}>
              <User className="w-4 h-4" />
              This actor
            </button>
            <button type="button" className="glass-button" onClick={investigateArea}>
              <Crosshair className="w-4 h-4" />
              This area
            </button>
            <Link to={nearbyHrefForEvent(event)} className="glass-button" title="Everyone recorded within 32 blocks, an hour either side">
              <MapPin className="w-4 h-4" />
              Who was here
            </Link>
            {canProtect && (
              <button type="button" className="btn-primary" onClick={() => setAttaching([event])}>
                <Lock className="w-4 h-4" />
                Add to a case
              </button>
            )}
          </>
        )}
      />

      <div className="glass-card p-4 md:p-5">
        <LogbookEventDetail
          event={event}
          server={server}
          canProtect={canProtect}
          onAddToCase={setAttaching}
          hideOpen
        />
      </div>

      {cases.length > 0 && (
        <div className="glass-card p-4 md:p-5">
          <SectionHeader title="Cases holding this record" icon={Lock} count={cases.length} />
          <ul className="divide-y divide-glass-200/10">
            {cases.map((entry) => (
              <li key={entry.id} className="py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                <Link to={`/logbook/cases/${entry.id}`} className="badge badge-accent font-mono">{entry.reference}</Link>
                <Link to={`/logbook/cases/${entry.id}`} className="text-sm text-white hover:underline truncate">{entry.title}</Link>
                <span className={entry.status === 'open' ? 'badge badge-success' : 'badge badge-neutral'}>{entry.status}</span>
                <span className="text-xs text-glass-500 tabular-nums ml-auto">{relativeTime(entry.updated_at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="glass-card overflow-hidden">
        <div className="p-4 md:p-5 pb-3">
          <SectionHeader
            title="The minute around this record"
            icon={Crosshair}
            count={nearby.length}
            description={`Everything captured within 16 blocks and 60 seconds on ${event.server_id}.`}
            actions={nearbyLoading ? <Loader className="w-4 h-4 animate-spin text-glass-400" /> : undefined}
          />
        </div>
        {nearby.length === 0 && !nearbyLoading ? (
          <div className="px-4 pb-6">
            <EmptyState
              bare
              compact
              icon={Crosshair}
              title="Nothing else was captured nearby"
              description={hasKnownTime(event)
                ? 'No other record within 16 blocks and 60 seconds.'
                : 'This record has no timestamp, so there is no window to search.'}
            />
          </div>
        ) : (
          <LogbookEventList
            runs={nearbyRuns}
            serversById={serversById}
            loading={nearbyLoading}
            hideServer
            canProtect={canProtect}
            heldEventIds={heldIds}
            onAddToCase={setAttaching}
          />
        )}
      </div>

      {attaching && (
        <AddToCaseModal
          events={attaching}
          onClose={() => setAttaching(null)}
          onAttached={() => setNonce((value) => value + 1)}
        />
      )}
    </div>
  );
}
